"use client";

import { Box, Flex, Icon } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { HiOutlineInformationCircle } from "react-icons/hi2";
import { Button, Link, Text } from "@/components";
import { Modal, ModalBody, ModalFooter } from "@/components/ui";
import { useThemeTokens } from "@/lib/theme/tokens";

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string | ReactNode;
  icon?: ReactNode;
  contactLink?: string;
  showContactLink?: boolean;
  "data-testid"?: string;
}

export const InfoModal = ({
  isOpen,
  onClose,
  title,
  message,
  icon,
  contactLink,
  showContactLink = false,
  "data-testid": dataTestId,
}: InfoModalProps) => {
  const theme = useThemeTokens();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size={{ base: "md", md: "lg" }}
      contentProps={dataTestId ? { "data-testid": dataTestId } : undefined}
    >
      <ModalBody py={6}>
        <Flex direction="column" align="center" gap={4}>
          {icon || (
            <Box
              w="64px"
              h="64px"
              borderRadius="full"
              bg={theme.accent.primaryBg}
              color={theme.accent.primary}
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <Icon as={HiOutlineInformationCircle} boxSize={8} />
            </Box>
          )}
          <Box w="100%">
            {typeof message === "string" ? (
              <Text
                fontSize="sm"
                color={theme.text.secondary}
                lineHeight="1.6"
                textAlign="center"
              >
                {message}
              </Text>
            ) : (
              <Box>{message}</Box>
            )}
            {showContactLink && contactLink && (
              <Box
                mt={4}
                pt={4}
                borderTopWidth="1px"
                borderTopColor={theme.borderGlass.default}
              >
                <Text
                  fontSize="sm"
                  color={theme.text.secondary}
                  mb={2}
                  textAlign="center"
                >
                  If you believe this is incorrect or have questions, please{" "}
                  <Link
                    href={contactLink}
                    color={theme.accent.primary}
                    fontWeight="500"
                    sx={{
                      "@media (hover: hover)": {
                        "&:hover": { textDecoration: "underline" },
                      },
                    }}
                  >
                    contact us
                  </Link>
                  .
                </Text>
              </Box>
            )}
          </Box>
        </Flex>
      </ModalBody>

      <ModalFooter
        px={{ base: 4, md: 6 }}
        py={4}
        borderTopWidth="1px"
        borderTopColor={theme.borderGlass.default}
        gap={3}
      >
        <Button
          variant="primary"
          onClick={onClose}
          flex={{ base: "1", sm: "none" }}
          {...(dataTestId
            ? { "data-testid": `${dataTestId}-confirm-button` }
            : {})}
          ml="auto"
        >
          Understood
        </Button>
      </ModalFooter>
    </Modal>
  );
};
